// The ITEM HISTORY ledger (brief §5.3.6): every state move, proposal round, verdict, execution and
// tag change with its reasoning inline — and REFUSALS rendered as attention rows, never swallowed.
// Rows render exactly what the per-item ledger projection carries (P-SELF; the displayed state
// derives from the owning module's rows — G-182); a superseded rival renders struck. Machine state
// rides data attributes (data-kind / data-outcome) so tests assert behaviour, not copy (G-180).

import type { ItemHistoryRow } from '../projections.ts';
import { WorkSectionHead } from '../work-ui.tsx';

/** One ledger row: when, kind, summary (+reasoning, +outcome). Refusals carry the attention tone. */
function HistoryRow({ row }: { readonly row: ItemHistoryRow }): React.JSX.Element {
  const attention = row.kind === 'refusal';
  return (
    <li
      data-kind={row.kind}
      data-outcome={row.outcome ?? ''}
      className="grid grid-cols-[4.5rem_6rem_1fr] items-baseline gap-4 border-b border-border px-1 py-2.5"
    >
      <span className="ritona-numeric text-xs text-text-subtle">{row.at}</span>
      <span
        className={`ritona-numeric text-xs uppercase ${attention ? 'font-medium text-bracken' : 'text-text-muted'}`}
      >
        {row.kind}
      </span>
      <span className="text-sm leading-snug text-text">
        <span className={row.outcome === 'superseded' ? 'text-text-subtle line-through' : ''}>
          {row.summary}
        </span>
        {row.reasoning !== null ? (
          <span className="mt-0.5 block text-xs italic text-text-muted">{row.reasoning}</span>
        ) : null}
        {row.outcome !== null ? (
          <span className="ritona-numeric mt-0.5 block text-xs text-text-subtle">
            {row.outcome}
          </span>
        ) : null}
      </span>
    </li>
  );
}

/** The per-item ledger section. */
export function HistoryList({
  history,
}: {
  readonly history: readonly ItemHistoryRow[];
}): React.JSX.Element {
  return (
    <section className="mt-8">
      <WorkSectionHead
        label="Item history"
        source="the per-item ledger · refusals surface here, never swallowed"
      />
      <ol aria-label="Item history" className="border-t border-border">
        {history.map((row, index) => (
          <HistoryRow key={index} row={row} />
        ))}
      </ol>
    </section>
  );
}
